import { InsightsApi } from '@insights/insights-data-provider';
import { isProduction } from '../../utils/platform';
import { getStoreId } from '../../utils/queryUtils';
import { getDexfSettings } from '../../services/dexfSettingsService';
import { getAnalyticsInteractionDevice } from '../utils/analyticsInteractionDevice';
import { getAnalyticsConsentStatus } from '../../cookies/getCookieConsentLevel';
import { getInsightsAppConnectData } from './getConnectInsightsData';
/**
 * Insights event service.
 */
export class InsightsService {
    insightsApi = new InsightsApi();
    isConnected = false;
    abTestExperiment = null;
    ongoingPromise = null;
    /**
     * Creates an instance of InsightsService.
     *
     * @param options Options.
     */
    constructor(options) {
        this.abTestExperiment = options.abTestExperiment;
    }
    getInsightsSessionId() {
        return this.insightsApi.getIds()?.sessionId ?? 'unknown';
    }
    getInsightsPlanningId() {
        return this.insightsApi.getIds()?.planningId ?? null;
    }
    /**
     * Sends init planning session event.
     *
     * @param event Event.
     */
    async sendInitPlanningSessionEvent(event) {
        return this.runIfConsentedTo(async () => {
            const payload = event.designId && event.vpcCode
                ? { ...event.payload, vpcCode: event.vpcCode }
                : event.payload;
            if (this.isConsoleOutputEnabled()) {
                // eslint-disable-next-line no-console
                console.log('InsightsService.sendInitPlanningSessionEvent', {
                    designId: event.designId || event.vpcCode || '',
                    source: event.source,
                    payload,
                    spr: event.spr
                });
            }
            this.insightsApi.initPlanningSession(event.designId || event.vpcCode || '', event.source, payload, event.spr);
        });
    }
    /**
     * Sends add to cart event.
     *
     * @param event Event.
     */
    async sendAddToCartEvent(event) {
        return this.runIfConsentedTo(async () => {
            const data = {
                currency: await this.getCurrencyCode(),
                designId: event.designId,
                items: event.articleData
                    ? event.articleData
                    : this.getValidShoppingItems(event.shoppingProducts),
                payload: {
                    index: event.index,
                    atcType: event.atcType
                }
            };
            if (this.isConsoleOutputEnabled()) {
                // eslint-disable-next-line no-console
                console.log('InsightsService.sendAddToCartEvent', data);
            }
            this.insightsApi.addToCart(data);
        });
    }
    /**
     * Sends add to wish list event.
     *
     * @param event Event.
     */
    async sendAddToWishListEvent(event) {
        return this.runIfConsentedTo(async () => {
            const data = {
                currency: await this.getCurrencyCode(),
                designId: event.designId,
                items: event.articleData
                    ? event.articleData
                    : this.getValidShoppingItems(event.shoppingProducts),
                payload: {
                    index: event.index,
                    atcType: event.atcType
                }
            };
            if (this.isConsoleOutputEnabled()) {
                // eslint-disable-next-line no-console
                console.log('InsightsService.sendAddToWishListEvent', data);
            }
            this.insightsApi.addToWishList(data);
        });
    }
    /**
     * Sends save design event.
     *
     * @param event Event.
     */
    async sendSaveDesignEvent(event) {
        return this.runIfConsentedTo(async () => {
            if (this.isConsoleOutputEnabled()) {
                // eslint-disable-next-line no-console
                console.log('InsightsService.sendSaveDesignEvent', {
                    designId: event.designId,
                    page: event.source
                });
            }
            this.insightsApi.saveDesign(event.designId, {
                page: event.source,
                vpcCode: event.vpcCode || ''
            });
        });
    }
    /**
     * Sends save design event.
     *
     * @param event Event.
     */
    async sendDesignInteractionEvent(event) {
        return this.runIfConsentedTo(async () => {
            if (this.isConsoleOutputEnabled()) {
                // eslint-disable-next-line no-console
                console.log('InsightsService.sendDesignInteractionEvent', {
                    designId: event.designId,
                    interaction: event.interaction
                });
            }
            this.insightsApi.sendDesignInteractionEvent(event.designId, event.interaction);
        });
    }
    /**
     * Sends custom event.
     *
     * @param event Event.
     */
    async sendCustomEvent(event) {
        return this.runIfConsentedTo(async () => {
            if (this.isConsoleOutputEnabled()) {
                // eslint-disable-next-line no-console
                console.log('InsightsService.sendCustomEvent', event);
            }
            await this.insightsApi.sendEvent(event);
        });
    }
    /**
     * Returns product items.
     *
     */
    async connect() {
        if (this.isConnected) {
            return;
        }
        if (this.ongoingPromise) {
            await this.ongoingPromise;
            return;
        }
        this.ongoingPromise = this.runIfConsentedTo(async () => {
            const applicationVersion = import.meta.env.VITE_VERSION ?? '';
            const data = getInsightsAppConnectData({
                abTestExperiment: this.abTestExperiment,
                applicationVersion
            });
            if (this.isConsoleOutputEnabled()) {
                // eslint-disable-next-line no-console
                console.log('InsightsService.connect', data);
            }
            await this.insightsApi.connectApplication(data);
            /* eslint-disable  no-console */
            console.log(`IPEX Insights Initialized (Dev: ${!isProduction()}, Version: ${applicationVersion}, Store ID: ${getStoreId()})`);
            this.isConnected = true;
        }, false);
        await this.ongoingPromise;
        this.ongoingPromise = null;
        return;
    }
    /**
     * Disconnects insights.
     */
    disconnect() {
        if (this.isConnected) {
            this.insightsApi.disconnect();
            this.insightsApi = new InsightsApi();
            this.isConnected = false;
            this.ongoingPromise = null;
        }
    }
    async runIfConsentedTo(onCookiesConfirmed, waitForConnect = true) {
        const analyticsConsentStatus = await getAnalyticsConsentStatus();
        if (analyticsConsentStatus !== 'ANALYTICS ALLOWED') {
            this.disconnect();
            return;
        }
        if (waitForConnect) {
            await this.connect();
        }
        return onCookiesConfirmed();
    }
    async getCurrencyCode() {
        const dexfSettings = await getDexfSettings();
        return dexfSettings.localisation.currencyCode;
    }
    /**
     * Returns true if console output is enabled.
     *
     * @return True if console output is enabled.
     */
    isConsoleOutputEnabled() {
        const params = new URLSearchParams(window.location.search);
        return (params.get('debugAnalytics') === 'true' || params.get('verboseAnalyticsLogging') === 'true');
    }
    /**
     * Returns valid shopping items.
     *
     * @param shoppingProducts Shopping products
     * @returns Shopping items
     */
    getValidShoppingItems(shoppingProducts) {
        const items = [];
        for (const shoppingProduct of shoppingProducts) {
            if (shoppingProduct.product?.content?.priceInformation) {
                const price = shoppingProduct.product.content.priceInformation.salesPrice.find(s => s.typeCode === 'familyInclTax' || 'inclTax')?.priceInclTax;
                if (price) {
                    items.push({
                        id: shoppingProduct.product.content.ruItemNo ||
                            shoppingProduct.product.content.itemNoGlobal ||
                            '',
                        qty: shoppingProduct.quantity,
                        price
                    });
                }
            }
        }
        return items;
    }
    /**
     * Reports custom event with scene articles and user context information (such as interaction device and location).
     *
     * @param event Event.
     * @param coreApi Core API.
     * @returns Promise.
     */
    async reportCustomEventWithSceneArticles(event, sceneArticles) {
        await this.sendCustomEvent({
            ...event,
            payload: {
                ...event.payload,
                sceneArticles,
                interactionDevice: getAnalyticsInteractionDevice(),
                location: location.href
            }
        });
    }
}
//# sourceMappingURL=InsightsService.js.map